import { buildHeader, buildParagraph } from "./helpers";
import { getMasteryLevel } from "../../utilities/masteryUtils";



const analyzeSkillMastery = (masteriesWithEmbeddedFocus) => {
  if (!masteriesWithEmbeddedFocus || masteriesWithEmbeddedFocus.length === 0) {
    return { hasRed: false, hasYellow: false, allGreen: false };
  }

  const levels = masteriesWithEmbeddedFocus.map(mastery => getMasteryLevel(mastery.currentPacing));
  const hasRed = levels.includes("red");
  const hasYellow = levels.includes("yellow");
  const allGreen = levels.every(level => level === "green");

  return { hasRed, hasYellow, allGreen };
};

const checkSkillProgression = (masteriesWithEmbeddedFocus) => {
  // For now, we'll use a simple heuristic based on current mastery levels
  // In a real implementation, this would compare beginning vs ending mastery
  if (!masteriesWithEmbeddedFocus || masteriesWithEmbeddedFocus.length === 0) {
    return false;
  }

  // Simplified logic: if student has any green skills, assume some progression
  // In reality, you'd compare initial vs current mastery levels
  return masteriesWithEmbeddedFocus.some(mastery => 
    getMasteryLevel(mastery.pacing) === "green"
  );
};

const checkUsageGoal = (metrics) => {
  // Define usage goal: at least 60 minutes of reading time per week
  const WEEKLY_USAGE_GOAL_MINUTES = 60;
  
  if (!metrics || !metrics.week || typeof metrics.week.timeRead !== 'object') {
    return false;
  }

  // Handle different possible structures for timeRead
  const weeklyMinutes = metrics.week.timeRead.sum || 
                       metrics.week.timeRead.total || 
                       metrics.week.timeRead || 0;

  // Convert seconds to minutes if necessary (assuming timeRead might be in seconds)
  const minutesRead = weeklyMinutes > 1000 ? weeklyMinutes / 60 : weeklyMinutes;
  

  
  return minutesRead >= WEEKLY_USAGE_GOAL_MINUTES;
};

const generateSummaryText = (studentData, usageGoalMet, skillAnalysis, skillProgression) => {
  const { firstName } = studentData;
  const { hasRed, hasYellow, allGreen } = skillAnalysis;

  // Determine mastery category
  let masteryCategory;
  if (hasRed) {
    masteryCategory = "red";
  } else if (hasYellow) {
    masteryCategory = "yellow";
  } else if (allGreen) {
    masteryCategory = "green";
  } else {
    masteryCategory = "unknown";
  }

  // Generate text based on the 12 scenarios
  if (usageGoalMet) {
    if (masteryCategory === "red") {
      if (skillProgression) {
        return `${firstName} met their weekly reading goal and showed improvement in reading skills, though some skills still need continued focus and practice.`;
      } else {
        return `${firstName} met their weekly reading goal but several reading skills still require additional support and practice to reach grade level.`;
      }
    } else if (masteryCategory === "yellow") {
      if (skillProgression) {
        return `${firstName} met their weekly reading goal and made progress in developing reading skills. Most skills are developing appropriately.`;
      } else {
        return `${firstName} met their weekly reading goal and is developing reading skills at an appropriate pace for their grade level.`;
      }
    } else if (masteryCategory === "green") {
      if (skillProgression) {
        return `${firstName} met their weekly reading goal and demonstrated excellent progress. They are mastering reading skills above grade level expectations.`;
      } else {
        return `${firstName} met their weekly reading goal and continues to demonstrate strong mastery of reading skills above grade level.`;
      }
    }
  } else {
    if (masteryCategory === "red") {
      if (skillProgression) {
        return `${firstName} did not meet their weekly reading goal but showed some improvement in reading skills. Increased practice time would help accelerate progress.`;
      } else {
        return `${firstName} did not meet their weekly reading goal and several reading skills need focused attention. More consistent practice is recommended.`;
      }
    } else if (masteryCategory === "yellow") {
      if (skillProgression) {
        return `${firstName} did not meet their weekly reading goal but made progress in developing reading skills. Additional practice time would support continued growth.`;
      } else {
        return `${firstName} did not meet their weekly reading goal. While reading skills are developing appropriately, more consistent practice would be beneficial.`;
      }
    } else if (masteryCategory === "green") {
      if (skillProgression) {
        return `${firstName} did not meet their weekly reading goal but continues to demonstrate strong reading skills above grade level. Consistent practice will maintain this excellence.`;
      } else {
        return `${firstName} did not meet their weekly reading goal but maintains strong mastery of reading skills. Regular practice sessions are encouraged.`;
      }
    }
  }

  // Fallback text
  return `${firstName} is progressing through their individualized reading plan. Continued practice and support will help achieve reading goals.`;
};

const createSectionOverallSummary = (studentData, weeklyPlan, metrics, masteriesWithEmbeddedFocus) => {
  // Analyze the data
  const usageGoalMet = checkUsageGoal(metrics);
  const skillAnalysis = analyzeSkillMastery(masteriesWithEmbeddedFocus);
  const skillProgression = checkSkillProgression(masteriesWithEmbeddedFocus);

  // Generate the appropriate summary text
  const summaryText = generateSummaryText(studentData, usageGoalMet, skillAnalysis, skillProgression);

  return [
    // EL Curriculum
    buildHeader(`${weeklyPlan.curTrack.label}`, 1, true),
    // Student's Summary Header
    buildHeader(`${studentData.firstName}'s SUMMARY`, 2),
    // Dynamic Summary Paragraph
    buildParagraph(summaryText, { italics: false }),
  ];
};

export default createSectionOverallSummary;
